import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Plane, Lock, User as UserIcon, ArrowRight, AlertTriangle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STRINGS = {
  it: {
    loading: 'Caricamento annuncio...',
    notFound: 'Link non valido',
    notFoundDesc: 'Il token di rivendicazione non è valido o è già stato utilizzato. Se hai già rivendicato l\'annuncio, accedi al tuo account per gestirlo.',
    goLogin: 'Vai al login',
    already: 'Annuncio già rivendicato',
    alreadyDesc: 'Questo annuncio è già stato associato ad un account. Accedi per gestirlo.',
    title: 'Rivendica il tuo annuncio',
    subtitle: 'Ti basta confermare l\'account per prendere il controllo dell\'annuncio.',
    yourListing: 'Il tuo annuncio',
    claimEmail: 'Email proprietario',
    hasAccount: 'Hai già un account AeroDeals con questa email.',
    hasAccountDesc: 'Clicca qui sotto per confermare e trasferire l\'annuncio sotto il tuo controllo.',
    newAccount: 'Creiamo il tuo account AeroDeals',
    newAccountDesc: 'Imposta una password per completare la rivendicazione. Userai questa email e password per accedere in futuro.',
    nameLabel: 'Nome',
    namePlaceholder: 'Il tuo nome',
    passwordLabel: 'Password',
    passwordPlaceholder: 'Almeno 6 caratteri',
    submit: 'Rivendica l\'annuncio',
    submitting: 'Rivendicazione in corso...',
    successTitle: 'Annuncio rivendicato',
    successDesc: 'Il tuo annuncio è ora collegato al tuo account. Vieni portato alla tua Dashboard dove potrai aggiornare foto, prezzo, dati tecnici e attivare In Evidenza.',
    goDash: 'Vai alla Dashboard',
    error: 'Errore durante la rivendicazione',
  },
  en: {
    loading: 'Loading listing...',
    notFound: 'Invalid link',
    notFoundDesc: 'The claim token is invalid or has already been used. If you already claimed the listing, log in to manage it.',
    goLogin: 'Go to login',
    already: 'Already claimed',
    alreadyDesc: 'This listing has already been linked to an account. Log in to manage it.',
    title: 'Claim your listing',
    subtitle: 'Just confirm the account to take control of the listing.',
    yourListing: 'Your listing',
    claimEmail: 'Owner email',
    hasAccount: 'You already have an AeroDeals account with this email.',
    hasAccountDesc: 'Click below to confirm and transfer the listing to your account.',
    newAccount: 'Let\'s set up your AeroDeals account',
    newAccountDesc: 'Set a password to complete the claim. You\'ll use this email + password to sign in going forward.',
    nameLabel: 'Name',
    namePlaceholder: 'Your name',
    passwordLabel: 'Password',
    passwordPlaceholder: 'At least 6 characters',
    submit: 'Claim listing',
    submitting: 'Claiming...',
    successTitle: 'Listing claimed',
    successDesc: 'Your listing is now linked to your account. You\'ll be taken to your Dashboard where you can update photos, price, specs and activate Featured.',
    goDash: 'Go to Dashboard',
    error: 'Error while claiming',
  },
};

export default function Claim() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const lang = data?.claim_email_lang === 'en' ? 'en' : 'it';
  const t = STRINGS[lang] || STRINGS.it;

  useEffect(() => {
    document.title = lang === 'en' ? 'Claim listing — AeroDeals' : 'Rivendica annuncio — AeroDeals';
    axios.get(`${API}/claim/${token}`)
      .then(r => {
        setData(r.data);
        if (r.data.existing_user?.name) setName(r.data.existing_user.name);
      })
      .catch(() => setErr('not_found'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErr('');
    try {
      const payload = {};
      if (!data.existing_user) {
        if (name.includes('@')) {
          setErr(lang === 'en' ? 'Name cannot contain an email address' : 'Il nome non può contenere un indirizzo email');
          setSubmitting(false);
          return;
        }
        payload.name = name;
        payload.password = password;
      }
      await axios.post(`${API}/claim/${token}`, payload, { withCredentials: true });
      setDone(true);
      await checkAuth();
      setTimeout(() => navigate('/dashboard'), 2500);
    } catch (e) {
      setErr(e.response?.data?.detail || t.error);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] pt-24 flex items-center justify-center px-4">
        <div className="text-zinc-500 text-sm">{STRINGS.it.loading}</div>
      </div>
    );
  }

  if (err === 'not_found' || !data) {
    return (
      <div className="min-h-screen bg-[#09090B] pt-24 flex items-center justify-center px-4" data-testid="claim-not-found">
        <div className="max-w-md w-full text-center">
          <AlertTriangle size={40} className="text-[#C5A059] mx-auto mb-4 opacity-70" />
          <h1 className="text-2xl md:text-3xl tracking-tight font-medium text-white mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {t.notFound}
          </h1>
          <p className="text-sm text-zinc-500 mb-6 leading-relaxed">{t.notFoundDesc}</p>
          <button onClick={() => navigate('/login')} className="bg-[#C5A059] text-black px-5 py-2.5 text-xs font-medium hover:bg-[#D4B271] transition-colors" data-testid="claim-goto-login">
            {t.goLogin}
          </button>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-[#09090B] pt-24 flex items-center justify-center px-4" data-testid="claim-success">
        <div className="max-w-md w-full text-center">
          <CheckCircle size={48} className="text-emerald-400 mx-auto mb-4" />
          <h1 className="text-2xl md:text-3xl tracking-tight font-medium text-white mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {t.successTitle}
          </h1>
          <p className="text-sm text-zinc-500 mb-6 leading-relaxed">{t.successDesc}</p>
          <button onClick={() => navigate('/dashboard')} className="bg-[#C5A059] text-black px-5 py-2.5 text-xs font-medium hover:bg-[#D4B271] transition-colors inline-flex items-center gap-2" data-testid="claim-goto-dashboard">
            {t.goDash} <ArrowRight size={14} />
          </button>
        </div>
      </div>
    );
  }

  if (data.already_claimed) {
    return (
      <div className="min-h-screen bg-[#09090B] pt-24 flex items-center justify-center px-4" data-testid="claim-already">
        <div className="max-w-md w-full text-center">
          <CheckCircle size={40} className="text-zinc-500 mx-auto mb-4" />
          <h1 className="text-2xl md:text-3xl tracking-tight font-medium text-white mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {t.already}
          </h1>
          <p className="text-sm text-zinc-500 mb-6 leading-relaxed">{t.alreadyDesc}</p>
          <button onClick={() => navigate('/login')} className="bg-[#C5A059] text-black px-5 py-2.5 text-xs font-medium hover:bg-[#D4B271] transition-colors" data-testid="claim-goto-login">
            {t.goLogin}
          </button>
        </div>
      </div>
    );
  }

  const L = data.listing || {};
  const priceFmt = L.price
    ? (L.currency === 'USD' ? '$' : '€') + Number(L.price).toLocaleString(lang === 'en' ? 'en-US' : 'it-IT')
    : '';

  return (
    <div className="min-h-screen bg-[#09090B] pt-20 pb-16 px-4" data-testid="claim-page">
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-8">
          <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#C5A059] mb-3">AeroDeals · Claim</p>
          <h1 className="text-3xl md:text-4xl tracking-tight font-medium text-white mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {t.title}
          </h1>
          <p className="text-sm text-zinc-500">{t.subtitle}</p>
        </div>

        {/* Listing card */}
        <div className="border border-zinc-800 bg-zinc-900/50 p-5 mb-6 flex items-start gap-4">
          <div className="w-14 h-14 shrink-0 bg-zinc-800 border border-zinc-700 flex items-center justify-center overflow-hidden">
            {L.photos?.[0] ? (
              <img src={L.photos[0]} alt="" className="w-full h-full object-cover" />
            ) : (
              <Plane size={20} className="text-zinc-600" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">{t.yourListing}</div>
            <div className="text-white text-base font-medium truncate" data-testid="claim-listing-title">{L.title}</div>
            <div className="text-xs text-zinc-500 mt-1">{L.manufacturer} {L.model} {L.year ? `· ${L.year}` : ''}{priceFmt ? ` · ${priceFmt}` : ''}</div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={submit} className="bg-zinc-900/50 border border-zinc-800 p-6 space-y-5">
          <div>
            <Label className="text-zinc-400 text-xs mb-1.5 block">{t.claimEmail}</Label>
            <div className="bg-zinc-800 border border-zinc-700 text-zinc-300 h-11 flex items-center px-3 text-sm" data-testid="claim-email-display">
              {data.claim_email}
            </div>
          </div>

          {data.existing_user ? (
            <div className="border-l-2 border-emerald-400/60 bg-emerald-400/5 px-4 py-3">
              <p className="text-emerald-300 text-sm font-medium">{t.hasAccount}</p>
              <p className="text-xs text-zinc-400 mt-1">{t.hasAccountDesc}</p>
            </div>
          ) : (
            <>
              <div className="border-l-2 border-[#C5A059]/70 bg-[#C5A059]/5 px-4 py-3">
                <p className="text-[#C5A059] text-sm font-medium">{t.newAccount}</p>
                <p className="text-xs text-zinc-400 mt-1">{t.newAccountDesc}</p>
              </div>
              <div>
                <Label className="text-zinc-400 text-xs mb-1.5 block">{t.nameLabel}</Label>
                <div className="relative">
                  <UserIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
                  <Input
                    type="text"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder={t.namePlaceholder}
                    className="pl-10 bg-zinc-900 border-zinc-800 text-white placeholder-zinc-600 h-11"
                    required
                    data-testid="claim-name-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-zinc-400 text-xs mb-1.5 block">{t.passwordLabel}</Label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
                  <Input
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder={t.passwordPlaceholder}
                    minLength={6}
                    className="pl-10 bg-zinc-900 border-zinc-800 text-white placeholder-zinc-600 h-11"
                    required
                    data-testid="claim-password-input"
                  />
                </div>
              </div>
            </>
          )}

          {err && err !== 'not_found' && (
            <p className="text-red-400 text-xs" data-testid="claim-error">{err}</p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#C5A059] text-black py-3 text-sm font-medium hover:bg-[#D4B271] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="claim-submit-btn"
          >
            {submitting ? t.submitting : t.submit}
            {!submitting && <ArrowRight size={16} />}
          </button>
        </form>
      </div>
    </div>
  );
}
